import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { useViewAs } from '../lib/viewAs';
import { Sidebar } from './Sidebar';
import { ViewAsBanner } from './ViewAsBanner';
import { MatchNightBanner } from './MatchNightBanner';
import { AccentInjector } from './AccentInjector';
import { HelpFab } from './HelpFab';
import { PageActionsProvider, ActivationBarSpacer, PageActionBar } from '../lib/pageActions';

// ─────────────────────────────────────────────────────────────────────────────
// Layout — workspace shell.
//
// Sidebar on the left (collapsible), main content on the right. View-as
// banner fixed at the top when a founder is impersonating. Match night
// banner appears above the main content area when there's a live match in
// the active comp.
//
// Mobile: a compact top bar with a hamburger button that slides the Sidebar
// in from the left as an off-canvas drawer.
// ─────────────────────────────────────────────────────────────────────────────

export function Layout({ children }: { children: React.ReactNode }) {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { viewAs, exit } = useViewAs();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const effectiveRole = viewAs?.role ?? me?.role;
  const bannerActive = Boolean(viewAs && me);

  return (
    <div className={cn('relative flex min-h-screen', bannerActive && 'pt-[46px]')}>
      <AccentInjector />
      {bannerActive && me && viewAs && (
        <ViewAsBanner viewAs={viewAs} realRole={me.role} onExit={exit} />
      )}

      {/* Sidebar */}
      <Sidebar
        me={me}
        effectiveRole={effectiveRole}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar (hamburger + brand) */}
        <header className="flex items-center justify-between gap-3 bg-paper-elevated px-4 py-3 md:hidden">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-white/[0.06] text-ink-secondary transition-colors hover:bg-white/[0.1] hover:text-ink-primary"
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <img src="/brand/gganvil-mark.svg" alt="GG Anvil" className="h-8 w-8" />
          <div className="w-9" />
        </header>

        {/* Match night banner (live matches in current comp) */}
        <MatchNightBanner />

        {/* Page content — PageActionsProvider is the global bottom action
            bar's registry (lib/pageActions.tsx): any page/component can
            register a copy of its own buttons via usePageActions/
            usePageActionNode, and PageActionBar below renders the union.
            ActivationBarSpacer owns the `--activation-bar-h` var + matching
            bottom padding globally, so SaveBar and each page's own trailing
            content clear the bar on EVERY page that has one, not just
            Setup (which used to own this locally). */}
        <PageActionsProvider>
          <main key={typeof window !== 'undefined' ? window.location.pathname : ''} className="flex-1 animate-forge-in px-4 py-8 sm:px-6 md:px-10">
            <ActivationBarSpacer className="mx-auto max-w-7xl">{children}</ActivationBarSpacer>
          </main>

          <HelpFab />

          {/* Footer — Anthropic-style hairline, no decorative gradient */}
          <footer className="relative mt-auto py-6">
            <div className="mx-auto flex max-w-7xl flex-col items-center gap-2 px-4 text-center">
              <img src="/brand/gganvil-text.svg" alt="GG Anvil" className="h-5 opacity-40" />
            </div>
          </footer>

          <PageActionBar />
        </PageActionsProvider>
      </div>
    </div>
  );
}
